import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FaArrowLeft, FaCertificate, FaCheck, FaTimes, FaEye, FaSearch, FaFileAlt, FaClock, FaUserCheck } from 'react-icons/fa';
import { certificationAPI } from '../../services/api';


const AdminCertifications = () => {
  const { user } = useSelector((state) => state.auth);

  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedCertification, setSelectedCertification] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchCertifications();
  }, []);

  const fetchCertifications = async () => {
    try {
      setLoading(true);
      const response = await certificationAPI.getAll({
        status: filterStatus || undefined,
        search: searchTerm || undefined
      });
      setCertifications(response.data.data.data || response.data.data);
    } catch (error) {
      console.error('Error fetching certifications:', error);
      setCertifications([]);
    } finally {
      setLoading(false);
    }
  };


  const handleApprove = async (certificationId) => {
    try {
      await certificationAPI.approve(certificationId, {
        admin_note: 'Approuvée par admin'
      });
      await fetchCertifications();
      alert('Certification approuvée avec succès!');
    } catch (error) {
      console.error('Error approving certification:', error);
      alert('Erreur lors de l\'approbation');
    }
  };


  const handleReject = async (certificationId) => {
    const reason = prompt('Motif du rejet (obligatoire):');
    if (!reason) return;
    
    try {
      await certificationAPI.reject(certificationId, {
        reason: reason,
        refund: true
      });
      await fetchCertifications();
      alert('Certification rejetée et remboursée!');
    } catch (error) {
      console.error('Error rejecting certification:', error);
      alert('Erreur lors du rejet');
    }
  };


  const handleViewDocument = async (certificationId) => {
    try {
      const response = await certificationAPI.viewDocument(certificationId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      alert('Erreur lors de l\'ouverture du document');
    }
  };


  const filteredCertifications = certifications.filter(certification => {
    const matchesSearch = certification.user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         certification.user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         certification.user.last_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || certification.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge bg-warning text-dark',
      approved: 'badge bg-success',
      rejected: 'badge bg-danger'
    };
    return badges[status] || 'badge bg-secondary';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'En attente',
      approved: 'Approuvée',
      rejected: 'Rejetée'
    };
    return texts[status] || status;
  };

  // Check if user is admin or superadmin
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-certifications min-vh-100" style={{ backgroundColor: 'var(--lova-bg-secondary)' }}>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-secondary me-3"
                  onClick={() => window.location.href = '/admin/dashboard'}
                >
                  <FaArrowLeft className="me-2" />
                  Retour
                </button>
                <div>
                  <h1 className="h2 mb-1">Gestion des Certifications</h1>
                  <p className="mb-0" style={{ color: 'var(--lova-text-secondary)' }}>Traiter les demandes de certification des utilisateurs</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaCertificate className="text-primary mb-2" size={24} />
                <h4 className="h5 mb-1">{certifications.length}</h4>
                <small style={{ color: 'var(--lova-text-secondary)' }}>Total Demandes</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaClock className="text-warning mb-2" size={24} />
                <h4 className="h5 mb-1">{certifications.filter(c => c.status === 'pending').length}</h4>
                <small className="text-muted">En Attente</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaUserCheck className="text-success mb-2" size={24} />
                <h4 className="h5 mb-1">{certifications.filter(c => c.status === 'approved').length}</h4>
                <small className="text-muted">Approuvées</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaTimes className="text-danger mb-2" size={24} />
                <h4 className="h5 mb-1">{certifications.filter(c => c.status === 'rejected').length}</h4>
                <small className="text-muted">Rejetées</small>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text"><FaSearch /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher par utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvées</option>
              <option value="rejected">Rejetées</option>
            </select>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center h-100">
              <small className="text-muted">{filteredCertifications.length} certification(s)</small>
            </div>
          </div>
        </div>

        {/* Certifications Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th className="border-0 ps-4">Utilisateur</th>
                          <th className="border-0">Type</th>
                          <th className="border-0">Statut</th>
                          <th className="border-0">Date de Demande</th>
                          <th className="border-0">Révisé par</th>
                          <th className="border-0 pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCertifications.map((certification) => (
                          <tr key={certification.id}>
                            <td className="ps-4">
                              <div>
                                <strong>{certification.user.first_name} {certification.user.last_name}</strong>
                                <br />
                                <small className="text-muted">@{certification.user.username}</small>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {certification.certification_type}
                              </span>
                            </td>
                            <td>
                              <span className={getStatusBadge(certification.status)}>
                                {getStatusText(certification.status)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(certification.created_at).toLocaleDateString('fr-FR')}
                              </small>
                            </td>
                            <td>
                              {certification.reviewed_by ? (
                                <small className="text-muted">@{certification.reviewed_by.username}</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="pe-4">
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-info"
                                  title="Voir document"
                                  onClick={() => handleViewDocument(certification.id)}
                                >
                                  <FaFileAlt />
                                </button>

                                <button
                                  className="btn btn-outline-primary"
                                  title="Voir détails"
                                  onClick={() => {
                                    setSelectedCertification(certification);
                                    setShowDetailsModal(true);
                                  }}
                                >
                                  <FaEye />
                                </button>
                                {certification.status === 'pending' && (
                                  <>
                                    <button
                                      className="btn btn-outline-success"
                                      title="Approuver"
                                      onClick={() => handleApprove(certification.id)}
                                    >
                                      <FaCheck />
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      title="Rejeter"
                                      onClick={() => handleReject(certification.id)}
                                    >
                                      <FaTimes />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {filteredCertifications.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              Aucune certification trouvée
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedCertification && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Détails de la Certification</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Informations Utilisateur</h6>
                    <p><strong>Nom:</strong> {selectedCertification.user.first_name} {selectedCertification.user.last_name}</p>
                    <p><strong>Username:</strong> @{selectedCertification.user.username}</p>
                    <p><strong>Type:</strong> {selectedCertification.certification_type}</p>
                    <p><strong>Statut:</strong> <span className={getStatusBadge(selectedCertification.status)}>{getStatusText(selectedCertification.status)}</span></p>
                  </div>
                  <div className="col-md-6">
                    <h6>Dates</h6>
                    <p><strong>Demandée le:</strong> {new Date(selectedCertification.created_at).toLocaleString('fr-FR')}</p>
                    {selectedCertification.reviewed_at && (
                      <p><strong>Révisée le:</strong> {new Date(selectedCertification.reviewed_at).toLocaleString('fr-FR')}</p>
                    )}
                    {selectedCertification.reviewed_by && (
                      <p><strong>Révisée par:</strong> @{selectedCertification.reviewed_by.username}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleViewDocument(selectedCertification.id)}
                  >
                    <FaFileAlt className="me-2" />
                    Voir le Document
                  </button>
                </div>

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                  Fermer
                </button>
                {selectedCertification.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => {
                        handleApprove(selectedCertification.id);
                        setShowDetailsModal(false);
                      }}
                    >
                      <FaCheck className="me-2" />
                      Approuver
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        handleReject(selectedCertification.id);
                        setShowDetailsModal(false);
                      }}
                    >
                      <FaTimes className="me-2" />
                      Rejeter
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .min-vh-100 {
          min-height: 100vh;
        }
        .table th {
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          background: var(--lova-bg-secondary) !important;
          color: var(--lova-text-primary) !important;
          border-bottom: 2px solid var(--lova-gray-light) !important;
        }
        .table td {
          vertical-align: middle !important;
          background: var(--lova-bg-primary) !important;
          color: var(--lova-text-primary) !important;
        }
        .table tbody tr {
          background: var(--lova-bg-primary) !important;
        }
        .table tbody tr:hover {
          background: var(--lova-bg-secondary) !important;
        }
        .modal-content {
          background: var(--lova-bg-primary) !important;
          color: var(--lova-text-primary) !important;
        }
        .modal-header, .modal-footer {
          border-color: var(--lova-gray-light) !important;
        }
      `}</style>
    </div>
  );
};

export default AdminCertifications;
